
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  Users, Search, Plus, RefreshCw, PencilLine, Trash2,
  Loader2, ShieldCheck, ChevronLeft, ChevronRight,
  UserCheck, UserX, AlertCircle, Power, ArrowUpDown,
  ArrowUp, ArrowDown
} from "lucide-react";

// ============================================================
// API CONFIGURATION
// ============================================================

const API = (process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com").replace(/\/$/, "");

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  timeout: 30000
});

// Match these paths with your App.js routes
const BASE_ROUTE = "/supremeadmin";
const LIST_ROUTE = `${BASE_ROUTE}/supremeAdminUsers_List`;
const ADD_ROUTE = `${BASE_ROUTE}/supremeAdminUsers_Add`;
const EDIT_ROUTE = `${BASE_ROUTE}/supremeAdminUsers_Edit`;
const DASHBOARD_ROUTE = `${BASE_ROUTE}/admin_Menu`;

const SORTABLE = [
  "username", "email", "display_name", "role",
  "is_active", "last_login_at", "created_at"
];

const fmtDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("en-IN");
};

const getError = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

const isActive = (value) => value === true || value === 1;

const escapeHtml = (value) =>
  String(value || "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }[character]));

const inputClass =
  "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10";

const secondaryButton =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";

const primaryButton =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 text-xs font-bold text-white transition hover:bg-emerald-800 disabled:opacity-50";

// ============================================================
// SORTABLE TABLE HEADER
// ============================================================

function SortHeader({ column, sortBy, sortDir, onSort, children }) {
  const active = sortBy === column;

  return (
    <th className="whitespace-nowrap px-3 py-3 text-left text-[11px] font-bold">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1.5 hover:text-emerald-600"
      >
        {children}
        {!active ? (
          <ArrowUpDown size={12} className="opacity-50" />
        ) : sortDir === "asc" ? (
          <ArrowUp size={12} />
        ) : (
          <ArrowDown size={12} />
        )}
      </button>
    </th>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function SuperAdminUsers_List() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / perPage)),
    [total]
  );

  const activeCount = useMemo(
    () => rows.filter((user) => isActive(user.is_active)).length,
    [rows]
  );

  const inactiveCount = useMemo(
    () => rows.filter((user) => !isActive(user.is_active)).length,
    [rows]
  );

  // ============================================================
  // SEARCH DEBOUNCE
  // ============================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  // ============================================================
  // FETCH USERS
  // ============================================================

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/superadmin/users", {
        params: { q, active, page, perPage, sortBy, sortDir },
        signal
      });

      const data = response.data;

      if (data?.ok === false || data?.success === false) {
        throw new Error(data.error || "Failed to fetch Supreme Admin users");
      }

      if (signal?.aborted) return;

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      if (axios.isCancel(err) || signal?.aborted) return;

      console.error("Supreme Admin users error:", err);
      setRows([]);
      setTotal(0);
      setError(getError(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [q, active, page, sortBy, sortDir]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);

  const refresh = () => setRefreshKey((value) => value + 1);

  // ============================================================
  // SORTING
  // ============================================================

  const toggleSort = (column) => {
    if (!SORTABLE.includes(column)) return;

    if (sortBy === column) {
      setSortDir((previous) => previous === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }

    setPage(1);
  };

  // ============================================================
  // DEACTIVATE / DELETE
  // ============================================================

  const deleteUser = async (user) => {
    if (deleting) return;

    const currentlyActive = isActive(user.is_active);

    const confirmation = await Swal.fire({
      icon: "warning",
      title: currentlyActive
        ? "Deactivate Supreme Admin?"
        : "Permanently Delete Supreme Admin?",
      html: currentlyActive
        ? `Are you sure you want to deactivate <b>${escapeHtml(user.username)}</b>?<br/><small>The account will no longer be able to log in.</small>`
        : `Are you sure you want to permanently delete <b>${escapeHtml(user.username)}</b>?<br/><small style="color:#dc2626">This action cannot be undone.</small>`,
      showCancelButton: true,
      confirmButtonText: currentlyActive
        ? "Yes, Deactivate"
        : "Yes, Delete Permanently",
      cancelButtonText: "Cancel",
      confirmButtonColor: currentlyActive ? "#d97706" : "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true
    });

    if (!confirmation.isConfirmed) return;

    const userId = String(user.user_id);
    setDeleting(userId);

    try {
      const response = await api.delete(
        `/superadmin/users/${encodeURIComponent(userId)}`
      );

      const data = response.data;

      if (data?.ok === false || data?.success === false) {
        throw new Error(data.error || "Operation failed");
      }

      await Swal.fire({
        icon: "success",
        title: data.status === "DEACTIVATED"
          ? "Account Deactivated"
          : "Account Deleted",
        text: data.message || "Operation completed successfully",
        confirmButtonColor: "#047857"
      });

      if (data.status === "DELETED" && rows.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        refresh();
      }
    } catch (err) {
      console.error("Supreme Admin operation error:", err);

      await Swal.fire({
        icon: "error",
        title: "Operation Failed",
        text: getError(err),
        confirmButtonColor: "#047857"
      });
    } finally {
      setDeleting("");
    }
  };

  // ============================================================
  // COMMON TABLE STYLES
  // ============================================================

  const tdClass = "px-3 py-3 text-xs text-slate-700";
  const actionButton =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-50";

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen bg-[#f5f8f6] text-slate-800">
      <div className="flex min-h-screen flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="min-w-0 w-full flex-1 overflow-auto px-4 py-5 md:px-6 lg:px-8">
          <div className="mx-auto max-w-[1500px]">

            {/* BREADCRUMB */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <Link
                  to={DASHBOARD_ROUTE}
                  className="hover:text-emerald-700"
                >
                  Supreme Admin
                </Link>
                <ChevronRight size={13} />
                <span className="font-bold text-emerald-700">
                  Supreme Admin Users
                </span>
              </div>
              <Time />
            </div>

            {/* PAGE HEADER */}
            <div className="mb-5 overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-l-4 border-emerald-600 px-5 py-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                      Infra V5 / Administration
                    </p>
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                      Supreme Admin Users
                    </h1>
                    <p className="mt-1 text-xs text-slate-500">
                      Manage global Supreme Admin accounts, permissions and account status.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    className={secondaryButton}
                  >
                    <RefreshCw
                      size={14}
                      className={loading ? "animate-spin" : ""}
                    />
                    Refresh
                  </button>

                  <Link to={ADD_ROUTE} className={primaryButton}>
                    <Plus size={15} />
                    Add New
                  </Link>
                </div>
              </div>
            </div>

            {/* SUMMARY CARDS */}
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                {
                  title: "Total Supreme Admins",
                  value: total,
                  icon: Users,
                  color: "bg-emerald-50 text-emerald-700"
                },
                {
                  title: "Active on This Page",
                  value: activeCount,
                  icon: UserCheck,
                  color: "bg-teal-50 text-teal-700"
                },
                {
                  title: "Inactive on This Page",
                  value: inactiveCount,
                  icon: UserX,
                  color: "bg-rose-50 text-rose-700"
                }
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div>
                      <p className="text-[11px] font-semibold text-slate-500">
                        {item.title}
                      </p>
                      <p className="mt-1 text-xl font-bold text-slate-900">
                        {item.value}
                      </p>
                    </div>
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.color}`}>
                      <Icon size={20} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* LIST SECTION */}
            <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

              {/* SECTION HEADER */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-100 bg-emerald-50/60 px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Users size={18} />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">
                      Supreme Admin List
                    </h2>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      View, search, edit and manage administrator accounts
                    </p>
                  </div>
                </div>
                <span className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-emerald-700">
                  {total} Records
                </span>
              </div>

              {/* SEARCH AND FILTER */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
                <div className="relative w-full max-w-md">
                  <Search
                    size={15}
                    className="absolute left-3 top-3 text-slate-400"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search username, email, display name..."
                    className={`${inputClass} pl-9`}
                  />
                </div>

                <select
                  value={active}
                  onChange={(event) => {
                    setActive(event.target.value);
                    setPage(1);
                  }}
                  className={`${inputClass} w-full sm:w-44`}
                >
                  <option value="">All Status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>

              {/* ERROR */}
              {error && (
                <div className="mx-5 mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{error}</span>
                  <button
                    type="button"
                    onClick={refresh}
                    className="ml-auto font-bold underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* RESPONSIVE TABLE */}
              <div className="min-w-0 p-4">
                <div className="relative w-full overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[1050px] border-collapse text-left">

                    <thead className="bg-emerald-50 text-emerald-900">
                      <tr>
                        <th className="w-14 px-3 py-3 text-left text-[11px] font-bold">
                          S.No
                        </th>

                        <SortHeader column="username" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Username
                        </SortHeader>

                        <SortHeader column="display_name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Display Name
                        </SortHeader>

                        <SortHeader column="email" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Email
                        </SortHeader>

                        <SortHeader column="role" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Role
                        </SortHeader>

                        <SortHeader column="is_active" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Status
                        </SortHeader>

                        <SortHeader column="last_login_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Last Login
                        </SortHeader>

                        <SortHeader column="created_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>
                          Created
                        </SortHeader>

                        {/* FIXED ACTION HEADER */}
                        <th className="sticky right-0 z-20 min-w-[110px] border-l border-emerald-100 bg-emerald-50 px-3 py-3 text-center text-[11px] font-bold shadow-[-4px_0_8px_-6px_rgba(0,0,0,0.25)]">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 bg-white">
                      {loading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-14 text-center">
                            <Loader2
                              size={22}
                              className="mx-auto mb-2 animate-spin text-emerald-600"
                            />
                            <p className="text-xs text-slate-500">
                              Loading Supreme Admin users...
                            </p>
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-14 text-center">
                            <Users
                              size={28}
                              className="mx-auto mb-2 text-slate-300"
                            />
                            <p className="text-sm font-semibold text-slate-600">
                              No Data Available
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              No Supreme Admin users match your filters.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        rows.map((user, index) => (
                          <tr
                            key={user.user_id}
                            className="group transition hover:bg-emerald-50/30"
                          >
                            <td className={`${tdClass} text-slate-500`}>
                              {(page - 1) * perPage + index + 1}
                            </td>

                            <td className={`${tdClass} whitespace-nowrap font-bold text-slate-800`}>
                              {user.username}
                            </td>

                            <td className={`${tdClass} whitespace-nowrap`}>
                              {user.display_name || "-"}
                            </td>

                            <td className={`${tdClass} whitespace-nowrap`}>
                              {user.email || "-"}
                            </td>

                            <td className={tdClass}>
                              <span className="inline-flex whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                {user.role === "SUPREME_ADMIN"
                                  ? "Supreme Admin"
                                  : user.role}
                              </span>
                            </td>

                            <td className={tdClass}>
                              <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                isActive(user.is_active)
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-red-50 text-red-600"
                              }`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${
                                  isActive(user.is_active)
                                    ? "bg-emerald-500"
                                    : "bg-red-400"
                                }`} />
                                {isActive(user.is_active) ? "Active" : "Inactive"}
                              </span>
                            </td>

                            <td className={`${tdClass} whitespace-nowrap text-slate-500`}>
                              {fmtDate(user.last_login_at)}
                            </td>

                            <td className={`${tdClass} whitespace-nowrap text-slate-500`}>
                              {fmtDate(user.created_at)}
                            </td>

                            {/* FIXED STICKY ACTION COLUMN */}
                            <td className="sticky right-0 z-10 min-w-[110px] border-l border-slate-100 bg-white px-3 py-3 shadow-[-4px_0_8px_-6px_rgba(0,0,0,0.25)] group-hover:bg-emerald-50">
                              <div className="flex items-center justify-center gap-2">

                                {/* EDIT */}
                                <Link
                                  to={`${EDIT_ROUTE}/${user.user_id}`}
                                  title="Edit Supreme Admin"
                                  className={`${actionButton} border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
                                >
                                  <PencilLine size={14} />
                                </Link>

                                {/* DEACTIVATE / DELETE */}
                                <button
                                  type="button"
                                  onClick={() => deleteUser(user)}
                                  disabled={Boolean(deleting)}
                                  title={isActive(user.is_active)
                                    ? "Deactivate Supreme Admin"
                                    : "Permanently Delete Supreme Admin"}
                                  className={`${actionButton} ${
                                    isActive(user.is_active)
                                      ? "border-amber-100 bg-amber-50 text-amber-700 hover:bg-amber-100"
                                      : "border-red-100 bg-red-50 text-red-600 hover:bg-red-100"
                                  }`}
                                >
                                  {deleting === String(user.user_id) ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : isActive(user.is_active) ? (
                                    <Power size={14} />
                                  ) : (
                                    <Trash2 size={14} />
                                  )}
                                </button>

                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* PAGINATION */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
                <p className="text-xs text-slate-500">
                  Showing {rows.length ? (page - 1) * perPage + 1 : 0}
                  {" - "}
                  {Math.min(page * perPage, total)}
                  {" of "}
                  {total} records
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={secondaryButton}
                  >
                    <ChevronLeft size={14} />
                    Previous
                  </button>

                  <span className="whitespace-nowrap text-xs font-bold text-slate-600">
                    Page {page} of {totalPages}
                  </span>

                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={secondaryButton}
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </section>

            {/* FOOTER */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
              <span>Infra V5 Supreme Admin / User Management</span>
              <span>Global Administrator Account Management</span>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}
